/*
 * @Author: Ryan
 * @Date: 2026-03-11
 * @Description: Old Way vs FalcoCut AI Twin Generator comparison section
 */
import Link from 'next/link';
import React from 'react';

const oldWayItems: string[] = [
  'Spend $500\u2013$2,000+ per day on gear and crew',
  'Requires 4+ hours of setup for a single 60-second ad',
  'Limited to filming 3\u20135 videos per day due to fatigue',
  'Wait 3\u20137 days for editors to send back a first draft',
  'Re-shooting a mistake costs 100% of the original budget',
];

const newWayItems: string[] = [
  'Create professional videos for under $1 per clip',
  'Go from script to final video in less than 2 minutes',
  'Generate 100+ unique videos daily with zero effort',
  'Receive a finished, high-quality export instantly',
  'Fix any script error with low cost',
];

const ArrowRightIcon: React.FC = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" width="1em" height="1em">
    <path d="M869 487.8L491.2 159.9c-2.9-2.5-6.6-3.9-10.5-3.9h-88.5c-7.4 0-10.8 9.2-5.2 14l350.2 304H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h585.1L386.9 854c-5.6 4.9-2.2 14 5.2 14h91.5c1.9 0 3.8-.7 5.2-2L869 536.2a32.07 32.07 0 000-48.4z" />
  </svg>
);

const ComparisonSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Old Way vs. FalcoCut AI Twin Generator
        </h2>
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-5xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          Producing high-quality content no longer requires a full camera crew or expensive
          studio rentals. FalcoCut allows you to create an AI twin in minutes, giving you
          the power to scale your brand presence without the typical headaches of manual
          filming.
        </p>

        {/* Comparison Cards */}
        <div className="grid grid-cols-1 pad:grid-cols-2 gap-6 pad:gap-8 mb-10 pad:mb-14">
          {/* Old Way Card */}
          <div className="rounded-2xl border border-gray-200 bg-white p-6 pad:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-xl pad:text-2xl font-bold text-gray-900">Old Way</h3>
            </div>
            <ul className="space-y-4 mb-6">
              {oldWayItems.map((item, index) => (
                <li key={index} className="flex">
                  <span className="text-gray-400 mr-2">&bull;</span>
                  <span className="text-sm pad:text-base text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-2 text-base font-semibold text-gray-800">
              <span>Manual, costly, and resource-heavy.</span>
            </div>
          </div>

          {/* New Way Card */}
          <div className="rounded-2xl border-2 border-[#6366F1] bg-[#6366F1]/5 p-6 pad:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-full bg-[#6366F1] flex items-center justify-center">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M13.5 4.5L6 12L2.5 8.5"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-xl pad:text-2xl font-bold text-gray-900">
                New Way with FalcoCut
              </h3>
            </div>
            <ul className="space-y-4 mb-6">
              {newWayItems.map((item, index) => (
                <li key={index} className="flex">
                  <span className="text-[#6366F1] mr-2">&bull;</span>
                  <span className="text-sm pad:text-base text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-2 text-base font-semibold text-[#6366F1]">
              <span>Instant, budget-friendly, and limitless.</span>
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <Link
            href="/ai-video-generator"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-white rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
            style={{
              background: 'linear-gradient(90deg, #4D2FFF 0%, #EF5CFF 100%)',
            }}
          >
            <span>Create Your AI Twin</span>
            <ArrowRightIcon />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;
